import {
    Alert,
    Paper,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    useMediaQuery,
    Grid,
    Card,
    CardContent,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormControlLabel,
    Switch,
    Typography,
    Tooltip,
    Link
} from '@mui/material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';

import { OptionsBase, OptionsSelectList, attrTypeLabelMap, useDivisionOptions, useTeamOptions, ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST, MAX_BODY_LENGTH } from '../../../state/master'
import { filterOptionsByOrg } from '../../../lib/filters';
import { apiClient } from '../../../lib/apiClient';
import { DIALOG_KEYS, UI_VISIBILITY } from '../../../constants';
import CommonSelect from '../../common/commonSelect/CommonSelect';
import PublishScopeDialog from '../../common/PublishScopeDialog/PublishScopeDialog';
import WeeklyReportPublishScopeDialog from './WeeklyReportPublishScopeDialog';
import { isWeeklyReportClassificationSetting } from './weeklyReportClassification';
import { PUBLIC_SCOPE_EDITED, prepareWeeklyReportPublicScopeOption } from './weeklyReportPublicScopePayload';
import OptionsAddDialog, { OptionsAddDialogResult } from './OptionsAddDialog';

import './SettingDialog.css';
import '../ReadOnlyTable.css';

const scopedAttributesInFlight = new Map<string, Promise<OptionsBase[]>>();

const normalizeAttributeResponse = (data: any): OptionsBase[] => {
    if (Array.isArray(data)) return data as OptionsBase[];
    if (Array.isArray(data?.data)) return data.data as OptionsBase[];
    if (Array.isArray(data?.list)) return data.list as OptionsBase[];
    return [];
};

// StrictMode などで同じモーダルの effect が続けて実行されても、同一組織への通信は共有する。
const fetchScopedAttributes = (departmentId: number, teamId: number): Promise<OptionsBase[]> => {
    const requestKey = `${departmentId}:${teamId}`;
    const pending = scopedAttributesInFlight.get(requestKey);
    if (pending) return pending;

    const request = apiClient.get('/get_attributes', {
        params: {
            department_id: departmentId,
            ...(teamId > 0 ? { team_id: teamId } : {}),
        },
    }).then((response) => normalizeAttributeResponse(response?.data));

    scopedAttributesInFlight.set(requestKey, request);
    request.finally(() => {
        if (scopedAttributesInFlight.get(requestKey) === request) {
            scopedAttributesInFlight.delete(requestKey);
        }
    }).catch(() => undefined);
    return request;
};

const normalizeAttributeName = (value: any): string => String(value ?? '').trim().toLowerCase();

const resolveScopedDialogOptions = (
    attributes: OptionsBase[],
    targetMeta: OptionsBase | undefined,
    title: string,
    departmentId: number,
    teamId: number,
): OptionsSelectList[] => {
    const targetName = normalizeAttributeName(targetMeta?.attr_name ?? targetMeta?.label ?? title);
    const targetIds = new Set<number>();
    [targetMeta?.attr_id, targetMeta?.logical_attr_id].forEach((value) => {
        const id = Number(value);
        if (Number.isFinite(id) && id > 0) targetIds.add(id);
    });

    const candidates = (Array.isArray(attributes) ? attributes : [])
        .filter((attribute: any) => {
            const attrId = Number(attribute?.attr_id);
            const logicalAttrId = Number(attribute?.logical_attr_id);
            const name = normalizeAttributeName(attribute?.attr_name ?? attribute?.label);
            return (
                (Number.isFinite(attrId) && targetIds.has(attrId)) ||
                (Number.isFinite(logicalAttrId) && targetIds.has(logicalAttrId)) ||
                (targetName !== '' && name === targetName)
            );
        })
        .sort((a: any, b: any) => {
            const score = (attribute: any): number => {
                const dep = Number(attribute?.department_id ?? 0);
                const targetTeam = Number(attribute?.team_id ?? 0);
                if (dep === departmentId && targetTeam === teamId) return 3;
                if (dep === departmentId && targetTeam === 0) return 2;
                if (dep === 0 && targetTeam === 0) return 1;
                return 0;
            };
            return score(b) - score(a);
        });

    const options = candidates.flatMap((attribute: any) =>
        Array.isArray(attribute?.options) ? attribute.options : []
    ) as OptionsSelectList[];
    return filterOptionsByOrg(options, departmentId, teamId);
};

function SettingDialog (props: any) {
    // プロパティの受け取り
    const {
        setting_id,
        division,
        team,
        title,
        meta,
        rows,
        dialogDivision,
        dialogFreeOption,
        dialogEditColor,
        dialogClassification,
        dialogFilterByTeam,
        onAccept,
        onClose,
        open,
        digKey,
        // 追加: Enlight連携関連（親から受け取る）
        enlightTeam,
        enlightSubteam,
        teamDocsetColumns,
        canEdit: canEditProp,
    } = props;
    const canEdit = canEditProp ?? true;

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const cardBg = isDark ? '#1e293b' : '#f0f0f0';
    const effectiveDialogClassification = dialogClassification ?? dialogEditColor;
    const isWeeklyReportClassification = isWeeklyReportClassificationSetting({
        settingId: setting_id,
        title,
        meta,
    });
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [dialogOpen, setDialogOpen] = useState(false);
    const [scopedOptionsLoading, setScopedOptionsLoading] = useState(false);
    const [scopedOptionsError, setScopedOptionsError] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const saveInFlightRef = useRef(false);
    const draftEditedRef = useRef(false);
    const editingSessionRef = useRef<string | null>(null);
    const editingSessionKey = JSON.stringify([
        setting_id, digKey, division, team, Boolean(dialogFilterByTeam),
    ]);

    const [optionsAddDialogOpen, setOptionsAddDialogOpen] = useState(false);

    // チーム設定: OptionsAddDialog の参照組織選択を SettingDialog 側で保持
    type OrgSelection = 'use' | 'no' | '';
    const [optionsAddOrgSelection, setOptionsAddOrgSelection] = useState<OrgSelection>('');
    const [optionsAddDivisionId, setOptionsAddDivisionId] = useState<number>(Number(division ?? 0));
    const [optionsAddTeamId, setOptionsAddTeamId] = useState<number>(Number(team ?? 0));

    // 他課公開ダイアログ（行単位）
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [publishDialogTarget, setPublishDialogTarget] = useState<{ index: number; area: 'enabled' | 'disabled' } | null>(null);
    const openPublishDialog = (index: number, area: 'enabled' | 'disabled') => {
        setPublishDialogTarget({ index, area });
        setPublishDialogOpen(true);
    };
    const closePublishDialog = () => {
        setPublishDialogOpen(false);
    };

    // 他課公開の適用: ダイアログで設定した公開スコープを該当オプションに反映
    const applyPublishScope = (next: {
        scope_type?: 'public' | 'teams' | 'department' | 'departments';
        department_ids?: number[];
        team_ids?: number[];
        private_flg: number;
        public_department: string | null;
        public_team: string | null;
    }) => {
        if (!publishDialogTarget) return;
        const idx = publishDialogTarget.index;
        setNewMeta((currentMeta) => {
            if (!Array.isArray(currentMeta?.options) || !currentMeta.options[idx]) return currentMeta;

            const options = currentMeta.options.map((option, optionIndex) => {
                if (optionIndex !== idx) return option;
                return {
                    ...option,
                    [PUBLIC_SCOPE_EDITED]: true,
                    public: {
                        ...next,
                        department_ids: Array.isArray(next.department_ids) ? [...next.department_ids] : [],
                        team_ids: Array.isArray(next.team_ids) ? [...next.team_ids] : [],
                    },
                } as OptionsSelectList;
            });

            return { ...currentMeta, options };
        });
    };

    const formatPublishScope = (pub: any) => {
        const scopeType = String(pub?.scope_type ?? '').trim();
        if (scopeType === 'teams') return '指定チームに公開';
        if (scopeType === 'department') return '課内公開';
        if (scopeType === 'departments') {
            const departmentIds = Array.isArray(pub?.department_ids) ? pub.department_ids : [];
            return departmentIds.length > 0 ? `指定課に公開（${departmentIds.join(', ')}）` : '指定課に公開';
        }
        if (scopeType === 'public') return '公開';
        const privateFlag = Number(pub?.private_flg);
        if (privateFlag === 1) return 'チーム内公開';
        if (privateFlag === 2) {
            const departments = String(pub?.public_department ?? '').trim();
            const currentDepartment = (Array.isArray(divisionOptions) ? divisionOptions : []).find(
                (item: any) => Number((item as any)?.department_id ?? (item as any)?.id) === Number(division)
            ) as any;
            const currentDepartmentCodes = [currentDepartment?.value, currentDepartment?.label]
                .map((value) => String(value ?? '').trim())
                .filter(Boolean);
            if (!departments || currentDepartmentCodes.includes(departments)) return '課内公開';
            return `指定課に公開（${departments}）`;
        }
        return '公開';
    };

    const getPublishScopeLabel = (row: OptionsSelectList) => {
        const pub = (row as any)?.public;
        if (isWeeklyReportClassification) return formatPublishScope(pub);
        const privateFlag = Number(pub?.private_flg);
        if (privateFlag === 1) return '非公開';
        if (privateFlag === 2) {
            const department = String(pub?.public_department ?? '').trim();
            return department ? `一部公開（${department}）` : '一部公開';
        }
        return '公開';
    };

    // 新規追加行に付与する安定識別子（ダイアログを開いている間、複数回追加しても衝突しない連番）
    const newOptionUidSeqRef = useRef(0);

    // Enlight 連携 UI: 使用するカラム選択（/get_docset_id_detail の fields.name 一覧）
    const [selectedEnlightColumnName, setSelectedEnlightColumnName] = useState<string>('');

    // /get_docset_id_detail 由来の detail を取得
    // 親（ParentConfig: 課/チーム）から呼び出された場合は使用しないため、APIも呼ばない
    const [detail, setDetail] = useState<any | null>(null);
    React.useEffect(() => {
        if (
            !open ||
            !enlightTeam ||
            digKey === DIALOG_KEYS.DIVISION ||
            digKey === DIALOG_KEYS.TEAM
        ) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        const run = async () => {
            try {
                const res = await apiClient.get('/get_docset_id_detail', {
                    params: {
                        enlight_team: String(enlightTeam),
                        ...(enlightSubteam ? { enlight_subteam: String(enlightSubteam) } : {}),
                    },
                });
                if (cancelled) return;
                setDetail(res?.data ?? null);
            } catch (e) {
                console.error('get_docset_id_detail error', e);
                if (!cancelled) setDetail(null);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [open, enlightTeam, enlightSubteam, digKey]);

    const enlightFields = React.useMemo(() => {
        const arr = (detail as any)?.json_data?.schema?.fields;
        return Array.isArray(arr) ? arr : [];
    }, [detail]);

    // Label/Value ともに fields[i].name を使用（絶対紐付け対象は候補から除外）
    const columnNameOptions = React.useMemo(() => {
        const names = enlightFields
            .map((f: any) => String(f?.name ?? '').trim())
            .filter(Boolean);

        // ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST のキーを除外（ChildConfig と同ロジック）
        const absoluteKeys = new Set<string>();
        (ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST ?? []).forEach((obj: any) => {
            const keys = Object.keys(obj ?? {});
            if (keys.length > 0) absoluteKeys.add(String(keys[0]));
        });

        const filtered = names.filter((nm) => !absoluteKeys.has(nm));
        return Array.from(new Set(filtered)).sort((a, b) => a.localeCompare(b));
    }, [enlightFields]);

    // 既に他属性で使用中の Enlight カラム名 -> attr_id 一覧（現在属性は除外）
    const linkedEnlightAttrIdsByName = React.useMemo(() => {
        const arr = Array.isArray(teamDocsetColumns) ? teamDocsetColumns : [];
        const currentAttrId = Number(setting_id);
        const map = new Map<string, number[]>();
        arr.forEach((c: any) => {
            const name = String(c?.enlight_column_name ?? '').trim();
            const attrId = Number(c?.attr_id);
            if (!name || !Number.isFinite(attrId)) return;
            if (Number.isFinite(currentAttrId) && attrId === currentAttrId) return;
            const list = map.get(name) ?? [];
            if (!list.includes(attrId)) list.push(attrId);
            map.set(name, list);
        });
        map.forEach((ids, key) => map.set(key, ids.sort((a, b) => a - b)));
        return map;
    }, [teamDocsetColumns, setting_id]);

    // 現在の属性に既に紐づいているカラム名（自身の選択値）
    const currentBoundEnlightColumnName = React.useMemo(() => {
        const id = Number(setting_id);
        if (!Number.isFinite(id)) return '';
        const found = (teamDocsetColumns ?? []).find((x: any) => Number(x?.attr_id) === id);
        return String(found?.enlight_column_name ?? '').trim();
    }, [setting_id, teamDocsetColumns]);

    // プルダウン候補:
    // - 連携済みも表示する（非表示にしない）
    // - ただし連携済み（他属性）は選択不可にする
    // - 現在の選択値は候補に含める
    const enlightSelectOptions = React.useMemo(() => {
        const base = [...(columnNameOptions ?? [])];
        // 現在の UI 選択値も選択肢に含める（存在しない場合は追加）
        const unionSet = new Set<string>(base);
        if (selectedEnlightColumnName) unionSet.add(selectedEnlightColumnName);
        if (currentBoundEnlightColumnName) unionSet.add(currentBoundEnlightColumnName);

        // columnNameOptions の並び順（文字昇順）に合わせて並べ替え
        const indexMap = new Map<string, number>((columnNameOptions ?? []).map((n: string, i: number) => [n, i]));
        const arr = Array.from(unionSet);
        arr.sort((a, b) => {
            const ai = indexMap.has(a) ? (indexMap.get(a) as number) : Number.MAX_SAFE_INTEGER;
            const bi = indexMap.has(b) ? (indexMap.get(b) as number) : Number.MAX_SAFE_INTEGER;
            return ai - bi || a.localeCompare(b);
        });
        return arr;
    }, [columnNameOptions, selectedEnlightColumnName, currentBoundEnlightColumnName]);

    // CommonSelect 用の options と value
    const enlightSelectOptionsForCommon = React.useMemo(() => {
        return (enlightSelectOptions ?? []).map((n: string) => {
            const linkedIds = linkedEnlightAttrIdsByName.get(n) ?? [];
            const linkedLabel = linkedIds.length > 0
                ? `${n}（連携済みID:${linkedIds.join(',')}）`
                : n;
            return {
                value: n,
                label: linkedLabel,
                isDisabled: linkedIds.length > 0 && n !== currentBoundEnlightColumnName,
            };
        });
    }, [enlightSelectOptions, linkedEnlightAttrIdsByName, currentBoundEnlightColumnName]);

    const selectedEnlightOption = React.useMemo(() => {
        const s = String(selectedEnlightColumnName ?? '').trim();
        if (!s) return null;
        const hit = (enlightSelectOptionsForCommon ?? []).find((o: any) => String(o?.value ?? '') === s);
        return hit ? { value: String(hit.value), label: String(hit.label) } : { value: s, label: s };
    }, [selectedEnlightColumnName, enlightSelectOptionsForCommon]);

    // ChildConfig から引き継いだ既存設定（teamDocsetColumns）で初期値を統一
    // 取得した候補（columnNameOptions）に存在しない場合は「未使用」にフォールバック
    React.useEffect(() => {
        const id = Number(setting_id);
        if (!Number.isFinite(id)) return;
        const found = (teamDocsetColumns ?? []).find((x: any) => Number(x?.attr_id) === id);
        const nm = String(found?.enlight_column_name ?? '').trim();
        if (nm && (columnNameOptions ?? []).includes(nm)) {
            setSelectedEnlightColumnName(nm);
        } else {
            setSelectedEnlightColumnName('');
        }
    }, [open, setting_id, teamDocsetColumns, columnNameOptions]);

    const [newMeta, setNewMetaState] = useState<OptionsBase>();
    // User edits mark the draft. Hydration uses the raw setter instead.
    const setNewMeta = React.useCallback((next: React.SetStateAction<OptionsBase | undefined>) => {
        draftEditedRef.current = true;
        setNewMetaState(next);
    }, []);
    // const [newOptions, setNewOptions] = useState<OptionsSelectList[]>(rows);


    // 表示用: division / team のラベル解決
    const { divisionOptions, divisionOptionsRaw } = useDivisionOptions();
    const { teamOptions, teamOptionsRaw } = useTeamOptions();

    const divisionLabel = React.useMemo(() => {
        const divIdNum = Number(division);
        if (Number.isNaN(divIdNum)) return '';
        const found = (divisionOptions ?? []).find(opt => Number(opt?.department_id) === divIdNum);
        return found?.label ?? (division != null ? String(division) : '');
    }, [division, divisionOptions]);

    const teamLabel = React.useMemo(() => {
        if (team == null) return '';
        const teamIdNum = Number(team);
        if (Number.isNaN(teamIdNum)) return '';
        const found = (teamOptions ?? []).find((opt: any) => Number((opt as any)?.team_id) === teamIdNum);
        return found?.label ?? String(team);
    }, [team, teamOptions]);

    const divisionLabelById = React.useMemo(() => {
        const map = new Map<number, string>();
        (divisionOptions ?? []).forEach((opt: any) => {
            const id = Number((opt as any)?.department_id);
            if (!Number.isFinite(id)) return;
            map.set(id, String((opt as any)?.label ?? ''));
        });
        return map;
    }, [divisionOptions]);

    const teamLabelById = React.useMemo(() => {
        const map = new Map<number, string>();
        (teamOptions ?? []).forEach((opt: any) => {
            const id = Number((opt as any)?.team_id);
            if (!Number.isFinite(id)) return;
            map.set(id, String((opt as any)?.label ?? ''));
        });
        return map;
    }, [teamOptions]);

    // 課（division）レベルの本文最大文字数を取得（/get_department 由来の divisionOptions から）
    // 注意: Number(null) は 0 になるため、そのまま数値化しない（null/undefined/'' は未設定扱い）
    // また 0 は「未設定」として扱い、UI では空表示にする
    const departmentBodyMax = React.useMemo(() => {
        const divIdNum = Number(division);
        if (Number.isNaN(divIdNum)) return undefined;
        const found = (divisionOptions ?? []).find((opt: any) => Number((opt as any)?.department_id) === divIdNum);
        const raw = (found as any)?.body_max_length;
        let v: number | undefined = undefined;
        if (typeof raw === 'number') {
            v = (Number.isFinite(raw) && raw > 0) ? raw : undefined;
        } else if (typeof raw === 'string') {
            const s = raw.trim();
            if (s !== '') {
                const n = Number(s);
                v = (Number.isFinite(n) && n > 0) ? n : undefined;
            }
        }
        return v;
    }, [division, divisionOptions]);

    // 旧形式の公開先コードも、保存前に正本のID配列へ変換する。
    const publicScopeContext = {
        departmentId: Number(division),
        teamId: Number(team),
        divisionOptions: divisionOptionsRaw ?? divisionOptions,
        teamOptions: teamOptionsRaw ?? teamOptions,
    };

    // 承諾（OK または YES ボタンをクリック）した時
    const handleAccept = async () => {
        if (saveInFlightRef.current || scopedOptionsLoading || scopedOptionsError) return;
        saveInFlightRef.current = true;
        setSaveError(null);
        setIsSaving(true);
        try {
        // enlight_column を生成:
        // - 未選択時: { attr_id: x } のみ
        // - 選択時  : { attr_id: x, enlight_column_name: "XXX", enlight_dtype: "YYY" }
        const attrIdFromMeta = Number((newMeta as any)?.attr_id);
        const baseAttrId = Number.isFinite(attrIdFromMeta) ? attrIdFromMeta : Number(setting_id);
        let enlight_column: any = { attr_id: Number.isFinite(baseAttrId) ? baseAttrId : -1 };
    
        const selName = String(selectedEnlightColumnName ?? '').trim();
        if (selName) {
            const f = (enlightFields ?? []).find((x: any) => String(x?.name ?? '').trim() === selName);
            const dtype = String(f?.enlight_dtype ?? '');
            enlight_column = {
                ...enlight_column,
                enlight_column_name: selName,
                enlight_dtype: dtype,
            };
        }
    
        // API更新完了後（MasterManage 側）に渡すペイロードへ付加
        // 項目表示は visible パラメータ名で送る（後方互換として status も 'Y'/'N' で維持）
        const base: any = { ...(newMeta as any) };
        const toBoolFromStatus = (v: any): boolean => typeof v === 'string' ? v === 'Y' : Boolean(v);
        base.visible = typeof base.visible === 'boolean' ? base.visible : toBoolFromStatus(base.status);
        if (typeof base.status === 'undefined') {
            base.status = base.visible ? 'Y' : 'N';
        } else if (typeof base.status === 'boolean') {
            base.status = base.status ? 'Y' : 'N';
        } else if (typeof base.status === 'string') {
            base.status = base.status === 'Y' ? 'Y' : 'N';
        }
    
        // パラメータ名のマッピング
        // - 本文最大文字数（課）: department_body_max_length
        // - 本文最大文字数（チームごと）: team_body_max_length
        // 入力必須: refresh_attributes 向けは input_required で送信する
        base.input_required = typeof base.input_required === 'boolean' ? base.input_required : Boolean(base.required);
        // 並び替えキーフラグ: boolean → 0/1 に正規化
        base.use_for_sort = base.use_for_sort ? 1 : 0;

        if (typeof base.body_max_length === 'number') {
            base.department_body_max_length = base.body_max_length > 0 ? base.body_max_length : null;
        } else if (isTeamDialog) {
            // 入力未設定時は NULL を明示送信
            base.department_body_max_length = null;
        }
        delete base.body_max_length;
    
        if (Array.isArray(base.options)) {
            base.options = (base.options as any[]).map((opt: any) => {
                const o = prepareWeeklyReportPublicScopeOption(
                    opt,
                    publicScopeContext,
                    isWeeklyReportClassification,
                );
                const uiRefOrg = (o as any).__ui_ref_org as any;
                if (uiRefOrg) {
                    (o as any).other_setting_using = uiRefOrg.orgSelection === 'use';
                    (o as any).using_department = Number(uiRefOrg.divisionId ?? 0);
                    (o as any).using_team = Number(uiRefOrg.teamId ?? 0);
                    if (uiRefOrg.orgSelection === 'use' && Boolean(uiRefOrg.copySameEnlightConnection)) {
                        (o as any).copy_same_enlight_connection = true;
                    } else {
                        delete (o as any).copy_same_enlight_connection;
                    }
                }
                if (typeof o.body_max_length === 'number') {
                    o.team_body_max_length = o.body_max_length;
                }
                delete o.body_max_length;
                delete (o as any).__ui_new;
                delete (o as any).__ui_ref_org;

                return o;
            });
        }
    
        const merged = { ...base, enlight_column };
        const acceptResult = await onAccept(merged);
        if (acceptResult !== false) {
            handleClose();
        }
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : '保存できませんでした。時間をおいて再度お試しください。');
        } finally {
            saveInFlightRef.current = false;
            setIsSaving(false);
        }
    };

    // ダイアログクローズ
    const handleClose = () => {
        setPublishDialogOpen(false);
        setDialogOpen(false);
        setOptionsAddDialogOpen(false);
        onClose();
    };

    // openの値が変化した時
    useEffect(() => {
        if (!open) {
            editingSessionRef.current = null;
            draftEditedRef.current = false;
            setDialogOpen(false);
            setOptionsAddDialogOpen(false);
            setPublishDialogOpen(false);
            return;
        }
        const newSession = editingSessionRef.current !== editingSessionKey;
        if (newSession) {
            editingSessionRef.current = editingSessionKey;
            draftEditedRef.current = false;
            setPublishDialogOpen(false);
            setPublishDialogTarget(null);
        }
        // A same-session background refresh must not replace the user's draft.
        if (!newSession && isWeeklyReportClassification &&
            (draftEditedRef.current || saveInFlightRef.current)) return;
        const next: OptionsBase = Object.assign({}, meta);
        // 呼び出し元は属性メタ情報と課・チームに対応する値一覧を分けて渡す。
        // APIレスポンスによって meta.options が空でも、rows に正しい値一覧があるため
        // rows を優先してダイアログの編集対象にする。
        const rowOptions = Array.isArray(rows) ? rows : undefined;
        const metaOptions = Array.isArray(next?.options) ? next.options : undefined;
        // rows は呼び出し時点の絞り込み結果だが、一時的な空配列の場合がある。
        // その場合に有効な meta.options を空配列で上書きしない。
        const sourceOptions = rowOptions && rowOptions.length > 0
            ? rowOptions
            : metaOptions && metaOptions.length > 0
                ? metaOptions
                : (rowOptions ?? metaOptions);
        if (next && Array.isArray(sourceOptions)) {
            next.options = sourceOptions;
            // ChildConfig 由来のダイアログのみ、division/team で options をフィルタ
            if (dialogFilterByTeam) {
                next.options = filterOptionsByOrg(
                    sourceOptions as OptionsSelectList[],
                    division as number,
                    team as number | undefined
                );
            }
        }

        // モーダル内の編集は「更新する」を押すまで親のマスタデータへ反映しない。
        // options と公開範囲を複製し、公開範囲ダイアログの「設定する」はローカル下書きだけを更新する。
        if (next && Array.isArray(next.options)) {
            next.options = next.options.map((option: any) => ({
                ...option,
                public: option?.public == null
                    ? option?.public
                    : {
                        ...option.public,
                        department_ids: Array.isArray(option.public.department_ids)
                            ? [...option.public.department_ids]
                            : option.public.department_ids,
                        team_ids: Array.isArray(option.public.team_ids)
                            ? [...option.public.team_ids]
                            : option.public.team_ids,
                    },
            })) as OptionsSelectList[];
        }

        // 表示フラグの互換対応: meta.visible があれば優先、それ以外は meta.status を使用（status が 'Y' のとき表示ON）
        const toBoolFromStatus = (v: any): boolean => typeof v === 'string' ? v === 'Y' : Boolean(v);
        const vis = (next as any)?.visible != null ? toBoolFromStatus((next as any).visible) : toBoolFromStatus((next as any).status);
        // status は元の型を保持（'Y'/'N' を壊さない）。UI の checked は visible を参照
        (next as any).visible = vis;

        // 入力必須の互換対応: required をUIで使用し、送信時は input_required を使用
        if (typeof (next as any).required === 'undefined' && typeof (next as any).input_required !== 'undefined') {
            (next as any).required = Boolean((next as any).input_required);
        }
        (next as any).input_required = Boolean((next as any).required);

        setNewMetaState(next);
        setSaveError(null);
        setDialogOpen(open);
        if (!open) setOptionsAddDialogOpen(false);
    }, [open, meta, rows, division, team, dialogFilterByTeam, editingSessionKey, isWeeklyReportClassification]);

    // 初期キャッシュに対象チームの値が無い場合だけ、選択中の課・チームを明示して再取得する。
    // マスタ設定画面の値・色・公開範囲を、Enlight のカラム取得成否から独立させる。
    useEffect(() => {
        const departmentId = Number(division ?? 0);
        const teamId = Number(team ?? 0);
        const hasScopedRows = filterOptionsByOrg(
            Array.isArray(rows) ? rows : [],
            departmentId,
            teamId,
        ).length > 0;
        const hasScopedMetaOptions = filterOptionsByOrg(
            Array.isArray(meta?.options) ? meta.options : [],
            departmentId,
            teamId,
        ).length > 0;

        if (
            !open ||
            !dialogFilterByTeam ||
            hasScopedRows ||
            hasScopedMetaOptions ||
            !Number.isFinite(departmentId) ||
            departmentId <= 0 ||
            !Number.isFinite(teamId) ||
            teamId <= 0
        ) {
            setScopedOptionsLoading(false);
            setScopedOptionsError(false);
            return undefined;
        }

        let cancelled = false;
        setScopedOptionsLoading(true);
        setScopedOptionsError(false);

        fetchScopedAttributes(departmentId, teamId)
            .then((attributes) => {
                if (cancelled) return;
                const recoveredOptions = resolveScopedDialogOptions(
                    attributes,
                    meta,
                    title,
                    departmentId,
                    teamId,
                );
                if (recoveredOptions.length === 0) return;

                setNewMetaState((currentMeta) => {
                    if (!currentMeta) return currentMeta;
                    // 通信中に追加・編集された内容を上書きしない。
                    if (Array.isArray(currentMeta.options) && currentMeta.options.length > 0) {
                        return currentMeta;
                    }
                    return {
                        ...currentMeta,
                        options: recoveredOptions.map((option: any) => ({
                            ...option,
                            public: option?.public == null
                                ? option?.public
                                : {
                                    ...option.public,
                                    department_ids: Array.isArray(option.public.department_ids)
                                        ? [...option.public.department_ids]
                                        : option.public.department_ids,
                                    team_ids: Array.isArray(option.public.team_ids)
                                        ? [...option.public.team_ids]
                                        : option.public.team_ids,
                                },
                        })),
                    };
                });
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('get_attributes error while recovering setting options', error);
                setScopedOptionsError(true);
            })
            .finally(() => {
                if (!cancelled) setScopedOptionsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, dialogFilterByTeam, rows, meta, title, division, team]);

    // オプション値の追加
    const addOptions = (
        initialValue: string = '',
        initialColor: string = '',
        uiRefOrg?: { orgSelection: OrgSelection; divisionId: number; teamId: number; copySameEnlightConnection?: boolean } | null
    ) => {
        // 「課を修正する」では division は使用しない（未設定のまま）
        const base: OptionsSelectList = {
            label: initialValue,
            color: initialColor,
            value: initialValue,
        };
        const newOption: OptionsSelectList = {
            ...base,
            ...(!isDivisionDialog ? ({ department_id: division } as any) : {}),
            ...(team != null && !isTeamDialog ? ({ team_id: team } as any) : {}),
        };
        (newOption as any).__ui_new = true;
        (newOption as any).__ui_uid = `new-${++newOptionUidSeqRef.current}`;
        if (uiRefOrg) {
            const sel = uiRefOrg.orgSelection === 'use' ? 'use' : 'no';
            (newOption as any).__ui_ref_org = {
                orgSelection: sel,
                divisionId: sel === 'use' ? Number(uiRefOrg.divisionId ?? 0) : 0,
                teamId: sel === 'use' ? Number(uiRefOrg.teamId ?? 0) : 0,
                copySameEnlightConnection: sel === 'use' ? Boolean((uiRefOrg as any)?.copySameEnlightConnection) : false,
            };
        }
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        reMeta.options && reMeta.options.push(newOption);
        setNewMeta(reMeta);
    };

    const handleAddClick = () => {
        if (isTeamDialog) {
            setOptionsAddOrgSelection('');
        }
        setOptionsAddDialogOpen(true);
    };

    const handleOptionsAddOk = (result: OptionsAddDialogResult) => {
        setOptionsAddDialogOpen(false);
        addOptions(
            String(result?.value ?? '').trim(),
            String(result?.color ?? '').trim(),
            result?.useExistingOrg
                ? {
                    orgSelection: 'use',
                    divisionId: result.divisionId,
                    teamId: result.teamId,
                    copySameEnlightConnection: Boolean((result as any)?.copySameEnlightConnection),
                }
                : { orgSelection: 'no', divisionId: 0, teamId: 0 }
        );
    };
    // オプション値の更新
    const updateOptions = (index: number, target: 'label' | 'value' | 'color' | 'body_max_length', value: string) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        reMeta.options && reMeta.options.map((opt, optIndex) => {
            if (index === optIndex) {
                if (target === 'body_max_length') {
                    // 数値に正規化（空文字なら未設定）
                    const v = value === '' ? undefined : Math.max(0, Math.floor(Number(value)));
                    (opt as any).body_max_length = v as any;
                } else if (target in opt) {
                    // 入力値をまず反映
                    (opt as any)[target] = value;

                    // 「入力中に空へクリア」する操作を阻害しないように、value -> label の一方向同期のみ行う
                    if (target !== 'color') {
                        const currValue = String((opt as any).value ?? '');
                        (opt as any).label = currValue;
                    }
                }
            }
            return opt;
        });
        setNewMeta(reMeta);
    };
    // オプションの有効/無効切替
    const setOptionStatus = (index: number, checked: boolean) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        reMeta.options && reMeta.options.map((opt, optIndex) => {
            if (index === optIndex) {
                (opt as any).status = checked ? 'Y' : 'N';
            }
            return opt;
        });
        setNewMeta(reMeta);
    };

    const removeOption = (index: number) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        if (!Array.isArray(reMeta.options)) return;
        reMeta.options = reMeta.options.filter((_opt: any, optIndex: number) => optIndex !== index) as any;
        setNewMeta(reMeta);
    };

    const handleDisableOption = (index: number, row: OptionsSelectList) => {
        if (Boolean((row as any).__ui_new)) {
            removeOption(index);
            return;
        }
        setOptionStatus(index, false);
    };

    // カラム名の変更
    const setColumnLabel = (value: string) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        reMeta.label = value;
        setNewMeta(reMeta);
    };

    // 入力方法の変更（attr_type に直接設定）
    const setInputAttrType = (value: number) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        reMeta.attr_type = value;
        setNewMeta(reMeta);
    };
    
    // トグル値の変更
    const setOptionBoolean = (target: 'required' | 'multiple_select' | 'status' | 'use_for_sort', checked: boolean) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        if (target === 'required') {
            reMeta.required = checked;
            (reMeta as any).input_required = checked;
        } else if (target === 'multiple_select') {
            reMeta.multiple_select = checked;
            // 複数選択ONにした場合は並び替えキーとして使えないのでフラグをOFF
            if (checked) reMeta.use_for_sort = false;
        } else if (target === 'use_for_sort') {
            reMeta.use_for_sort = checked;
        } else {
            // 項目表示: status は 'Y'/'N' を使用し、visible は boolean を保持
            (reMeta as any).status = checked ? 'Y' : 'N';
            (reMeta as any).visible = checked;
        }
        setNewMeta(reMeta);
    };

    // 課ダイアログか判定（DigKeyを使用）
    const isDivisionDialog = digKey === DIALOG_KEYS.DIVISION;
    const isTeamDialog = digKey === DIALOG_KEYS.TEAM;

    React.useEffect(() => {
        if (!open || !isTeamDialog) return;
        setOptionsAddDivisionId((prev) => prev || Number(division ?? 0));
        setOptionsAddTeamId((prev) => prev || Number(team ?? 0));
    }, [open, isTeamDialog, division, team]);

    // チームダイアログ: 本文最大文字数 入力用のローカル状態（未設定時のフォールバック値も value として表示）
    const [deptBodyMaxInput, setDeptBodyMaxInput] = React.useState<string>('');
    React.useEffect(() => {
        // 初期表示時のみ同期（編集中は上書きしない）
        if (!isTeamDialog) return;
        if (!open) return;
        const metaVal = (newMeta as any)?.body_max_length;
        if (typeof metaVal === 'number' && Number.isFinite(metaVal) && metaVal > 0) {
            setDeptBodyMaxInput(String(metaVal));
        } else {
            setDeptBodyMaxInput(typeof departmentBodyMax === 'number' ? String(departmentBodyMax) : '');
        }
    }, [open, isTeamDialog]);

    // テキスト入力: 文字数制限（最小/最大）の設定と制約(最大 >= 最小)
    const setTextLength = (target: 'min' | 'max', raw: string) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        const v = raw === '' ? undefined : Math.max(0, Math.floor(Number(raw)));
        if (target === 'min') {
            (reMeta as any).min_length = v as any;
        } else {
            (reMeta as any).max_length = v as any;
        }
        const minL = typeof (reMeta as any).min_length === 'number' ? (reMeta as any).min_length : undefined;
        const maxL = typeof (reMeta as any).max_length === 'number' ? (reMeta as any).max_length : undefined;
        if (typeof minL === 'number' && typeof maxL === 'number' && maxL < minL) {
            (reMeta as any).max_length = minL;
        }
        setNewMeta(reMeta);
    };

    // 本文最大文字数（メタ）設定（空なら未設定, 0 も未設定扱い）
    const setBodyMaxLengthMeta = (raw: string) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        const s = String(raw ?? '').trim();
        let v: number | undefined = undefined;
        if (s !== '') {
            const n = Math.floor(Number(s));
            v = (Number.isFinite(n) && n > 0) ? n : undefined;
        }
        (reMeta as any).body_max_length = v as any;
        setNewMeta(reMeta);
    };

    // テキスト属性かどうか、およびバリデーション状態
    const isTextAttr = Boolean(newMeta && (newMeta.attr_type === 1 || newMeta.attr_type === 2));
    const minLen = typeof (newMeta as any)?.min_length === 'number' ? (newMeta as any).min_length : undefined;
    const maxLen = typeof (newMeta as any)?.max_length === 'number' ? (newMeta as any).max_length : undefined;
    const textLenInvalid = Boolean(isTextAttr && minLen != null && maxLen != null && maxLen < minLen);

    // 入力必須チェック（カラム名・入力方法）
    const isColumnNameMissing = !(newMeta && String(((newMeta as any)?.label ?? '')).trim().length > 0);
    const isInputTypeMissing = !(newMeta && (newMeta as any)?.attr_type !== undefined && (newMeta as any)?.attr_type !== null && String((newMeta as any)?.attr_type) !== '');
    // 更新ボタンの無効条件: テキスト長エラー または 必須未入力（カラム名/入力方法）
    const isUpdateDisabled = Boolean((isTextAttr && textLenInvalid) || isColumnNameMissing || isInputTypeMissing);
    
    // ===== セクション内リンク用: ref/可視フラグ/スクロール関数 =====
    const refCol = React.useRef<HTMLDivElement>(null);
    const refEnlight = React.useRef<HTMLDivElement>(null);
    const refText = React.useRef<HTMLDivElement>(null);
    const refOptions = React.useRef<HTMLDivElement>(null);
    
    const showEnlight = UI_VISIBILITY.ENLIGHT_LINKAGE_SETTINGS
        && !(digKey === DIALOG_KEYS.DIVISION || digKey === DIALOG_KEYS.TEAM)
        && String(enlightTeam ?? '').trim() !== '';
    const showText = Boolean(newMeta && (newMeta.attr_type === 1 || newMeta.attr_type === 2 || newMeta.attr_type === 13));
    const showOptions = Boolean(newMeta && (newMeta.attr_type === 11 || newMeta.attr_type === 12 || newMeta.attr_type === 13));
    const sortDisabledReason = dialogDivision
        ? '課・チームの設定では並び替えキーは設定できません'
        : !showOptions
        ? '入力方法が選択肢型（単一行選択・複数行選択・プルダウン）の場合のみ設定できます'
        : newMeta?.multiple_select
        ? '「複数選択」が無効の場合のみ設定できます'
        : '';

    const scrollToRef = (r: React.RefObject<any>) => {
      r.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Enlight同期デフォルトの正規化（1/0 -> boolean）
    const normalizeEnlightSyncDefault = (v: any): boolean => Number(v) === 1;

    // Enlight同期デフォルトは 1/0 のみを使用
    const toEnlightSyncDefaultValue = (_orig: any, checked: boolean): number => (checked ? 1 : 0);
    // Enlight同期デフォルトの切替（課のときのみ有効、値は 1/0）
    const setEnlightSyncDefault = (index: number, checked: boolean) => {
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        reMeta.options && reMeta.options.map((opt, optIndex) => {
            if (index === optIndex) {
                (opt as any).enlight_sync_default = checked ? 1 : 0;
            }
            return opt;
        });
        setNewMeta(reMeta);
    };

    // 並び替え（有効エリアのみ）
    const findPrevEnabledIndex = (currentIndex: number): number => {
        const opts = newMeta?.options;
        if (!opts) return -1;
        for (let i = currentIndex - 1; i >= 0; i--) {
            if ((opts[i] as any).status !== 'N') return i;
        }
        return -1;
    };

    const findNextEnabledIndex = (currentIndex: number): number => {
        const opts = newMeta?.options;
        if (!opts) return -1;
        for (let i = currentIndex + 1; i < opts.length; i++) {
            if ((opts[i] as any).status !== 'N') return i;
        }
        return -1;
    };

    const swapOptionsAt = (i: number, j: number) => {
        if (!newMeta?.options) return;
        if (i < 0 || j < 0 || i === j) return;
        const reMeta: OptionsBase = Object.assign({}, newMeta);
        const opts = reMeta.options!;
        const tmp = opts[i];
        opts[i] = opts[j];
        opts[j] = tmp;
        setNewMeta(reMeta);
    };

    const moveUp = (currentIndex: number) => {
        const prev = findPrevEnabledIndex(currentIndex);
        if (prev !== -1) swapOptionsAt(currentIndex, prev);
    };

    const moveDown = (currentIndex: number) => {
        const next = findNextEnabledIndex(currentIndex);
        if (next !== -1) swapOptionsAt(currentIndex, next);
    };

    return (
        <Dialog
            open={dialogOpen}
            maxWidth={false}
            fullScreen={fullScreen}
            fullWidth
            PaperProps={{ sx: { width: { xs: '95vw', sm: 1050 }, maxWidth: 'calc(100vw - 32px)' } }}
        >
            <DialogTitle>
                <span>
                    {title}
                    {!isDivisionDialog && divisionLabel
                        ? ` [${divisionLabel}${((digKey !== DIALOG_KEYS.TEAM) && teamLabel) ? ` - ${teamLabel}` : ''}]`
                        : ''}
                </span>
            </DialogTitle>
            {/*
            <Box sx={{ px: 3, pt: 1, pb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>ジャンプ:</Typography>
              <Link component="button" underline="hover" onClick={() => scrollToRef(refCol)}>カラム名と入力/表示</Link>
              {showEnlight && (
                <Link component="button" underline="hover" onClick={() => scrollToRef(refEnlight)}>Enlight連携設定</Link>
              )}
              {showText && (
                <Link component="button" underline="hover" onClick={() => scrollToRef(refText)}>テキスト入力設定</Link>
              )}
              {showOptions && (
                <Link component="button" underline="hover" onClick={() => scrollToRef(refOptions)}>値・色設定、および有効/無効化</Link>
              )}
            </Box>
            */}
            <DialogContent >
                {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
                <Box className="setting-dialog-root">
                    {/* カラム名と入力/表示 */}
                    <Card ref={refCol} sx={{ backgroundColor: cardBg, marginBottom: 2, position: 'relative' }}>
                        <Typography variant="caption" sx={{ position: 'absolute', top: 6, left: 8, fontWeight: 600, zIndex: 1 }}>
                            カラム名と入力/表示
                        </Typography>
                        <CardContent sx={{ pt: 3 }}>
                            <Grid container className="search-row">
                                <Grid size={3} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                    <InputLabel
                                        htmlFor="column-name"
                                        sx={{
                                            minHeight: 32,
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: 1,
                                        }}
                                    >
                                        カラム名
                                    </InputLabel>
                                    <TextField
                                        required
                                        id="column-name"
                                        sx={{
                                            backgroundColor: isDark ? '#1e293b' : 'white',
                                            minHeight: 40,
                                            '.MuiInputBase-input': { height: 40, boxSizing: 'border-box', padding: '8px 14px' }
                                        }}
                                        value={newMeta && newMeta.label}
                                        disabled={!canEdit || dialogDivision || !dialogFreeOption}
                                        onChange={(event) => setColumnLabel(event.target.value)}
                                    />
                                </Grid>
                                <Grid size={3} display="flex" justifyContent="center" alignItems="center">
                                    <Grid size={12} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                        <InputLabel
                                            htmlFor="input-type"
                                            sx={{
                                                minHeight: 32,
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 1,
                                            }}
                                        >
                                            入力方法
                                        </InputLabel>
                                        <Select
                                            required
                                            id="input-type"
                                            sx={{
                                                backgroundColor: isDark ? '#1e293b' : 'white',
                                                minHeight: 40,
                                                '.MuiSelect-select': { height: 40, boxSizing: 'border-box', padding: '8px 14px' }
                                            }}
                                            // 共通項目の設定変更では入力方法の変更不可（freeOption=false のとき無効化）
                                            disabled={!canEdit || dialogDivision || !dialogFreeOption}
                                            value={(newMeta && newMeta.attr_type !== undefined) ? newMeta.attr_type : ''}
                                            onChange={(event) => setInputAttrType(Number(event.target.value))}
                                        >
                                            {Object.entries(attrTypeLabelMap).map(([val, label]) => (
                                                <MenuItem key={val} value={Number(val)}>{label}</MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                </Grid>
                                {isTeamDialog && (
                                    <Grid size={3} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                        <InputLabel
                                            htmlFor="dept-body-max"
                                            sx={{
                                                minHeight: 32,
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 1,
                                            }}
                                        >
                                            本文最大文字数（課）
                                        </InputLabel>
                                        <TextField
                                            id="dept-body-max"
                                            type="number"
                                            inputProps={{ min: 0, step: 1 }}
                                            sx={{
                                                backgroundColor: isDark ? '#1e293b' : 'white',
                                                minHeight: 40,
                                                '.MuiInputBase-input': { height: 40, boxSizing: 'border-box', padding: '8px 14px' }
                                            }}
                                            value={deptBodyMaxInput}
                                            onChange={(event) => { setDeptBodyMaxInput(event.target.value); setBodyMaxLengthMeta(event.target.value); }}
                                            placeholder={ 'Default:' + MAX_BODY_LENGTH }
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                )}
                                {dialogFreeOption && !dialogDivision && (
                                    <Grid size={2} display="flex" justifyContent="center" alignItems="center">
                                        <FormControlLabel label="入力必須" labelPlacement="top" control={
                                            <Switch
                                                disabled={!canEdit || dialogDivision}
                                                checked={newMeta && newMeta.required}
                                                onChange={(event) => setOptionBoolean("required", event.target.checked)}
                                            />
                                        }></FormControlLabel>
                                    </Grid>
                                )}
                                {!dialogFreeOption && !dialogDivision && (
                                    <Grid size={2} display="flex" justifyContent="center" alignItems="center">
                                        <FormControlLabel label="項目表示" labelPlacement="top" control={
                                            <Switch
                                                disabled={!canEdit || dialogDivision}
                                                checked={((newMeta as any)?.visible != null) ? Boolean((newMeta as any)?.visible) : (String((newMeta as any)?.status) === 'Y')}
                                                onChange={(event) => setOptionBoolean("status", event.target.checked)}
                                            />
                                        }></FormControlLabel>
                                    </Grid>
                                )}
                                {!dialogDivision && showOptions && (
                                    <Grid size={2} display="flex" justifyContent="center" alignItems="center">
                                        <FormControlLabel label="複数選択" labelPlacement="top" control={
                                            <Switch
                                                disabled={!canEdit || dialogDivision}
                                                checked={newMeta && newMeta.multiple_select}
                                                onChange={(event) => setOptionBoolean("multiple_select", event.target.checked)}
                                            />
                                        }></FormControlLabel>
                                    </Grid>
                                )}
                                <Grid size={2} display="flex" justifyContent="center" alignItems="center">
                                    <Tooltip title={sortDisabledReason} placement="top" arrow>
                                        <span style={{ display: 'inline-flex' }}>
                                            <FormControlLabel label="並び替えキー" labelPlacement="top" control={
                                                <Switch
                                                    disabled={!canEdit || !!sortDisabledReason}
                                                    checked={Boolean(newMeta?.use_for_sort)}
                                                    onChange={(event) => setOptionBoolean("use_for_sort", event.target.checked)}
                                                />
                                            }></FormControlLabel>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    {/* カラム名と入力/表示 */}
                    {/* Enlight連携設定（課/チームの設定ダイアログから呼び出された場合は非表示） */}
                    {showEnlight && (
                        <Card ref={refEnlight} sx={{ backgroundColor: cardBg, mb: 2, position: 'relative' }}>
                            <Typography variant="caption" sx={{ position: 'absolute', top: 6, left: 8, fontWeight: 600, zIndex: 1 }}>
                                Enlight連携設定
                            </Typography>
                            <CardContent sx={{ pt: 4 }}>
                                <Grid container className="search-row" rowSpacing={1} columnSpacing={2}>
                                    <Grid size={4} offset={0.5} display="flex" flexDirection="column" alignItems="flex-start">
                                        {/* グループ1: EnlightチームID（ラベル + 値を横並び） */}
                                        <Grid size={12} sx={{ mb: 2 }} display="flex" flexDirection="column" alignItems="flex-start">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.25 }}>
                                                    連携されているEnlightチームID：
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ fontWeight: 700, wordBreak: 'break-all', pl: 2 }}
                                                >
                                                    {String(enlightTeam ?? '')}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {/* グループ2: 使用するEnlightカラム名（ラベル + プルダウン） */}
                                        <Grid size={12} display="flex" flexDirection="column" alignItems="flex-start">
                                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                使用するEnlightカラム名
                                            </Typography>
                                            <Box sx={{ width: '100%' }}>
                                                <CommonSelect
                                                    id="enlight-column-name"
                                                    options={enlightSelectOptionsForCommon}
                                                    value={selectedEnlightOption}
                                                    onChange={(opt) => setSelectedEnlightColumnName(opt?.value ?? '')}
                                                    placeholder="未設定"
                                                    isClearable
                                                    isDisabled={!canEdit}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <Grid size={7} display="flex" flexDirection="column" alignItems="flex-start">
                                        <Box sx={{ px: 0, pb: 1, borderBottom: '1px solid', borderColor: 'divider', width: '100%' }}>
                                            <Typography variant="caption" fontWeight="bold">Enlightカラム名一覧</Typography>
                                        </Box>
                                        {String(enlightTeam ?? '') === '' ? (
                                            <Box sx={{ p: 2, color: 'text.secondary' }}>「未連携」のため、表示できません。</Box>
                                        ) : !detail ? (
                                            <Box sx={{ p: 2, color: 'text.secondary' }}>読込中...</Box>
                                        ) : (
                                            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 320, width: '100%' }}>
                                                <Table stickyHeader size="small" aria-label="Enlightカラム" sx={{ tableLayout: 'fixed' }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ width: '50%' }}>カラム名</TableCell>
                                                            <TableCell sx={{ width: '25%' }}>種別</TableCell>
                                                            <TableCell sx={{ width: '25%' }}>説明</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {(() => {
                                                            // プルダウンと同一のフィルタ結果のみ表示（絶対紐付けは除外済み）
                                                            const nameSet = new Set<string>(columnNameOptions);
                                                            const rows = (enlightFields ?? []).filter((f: any) => {
                                                                const n = String(f?.name ?? '').trim();
                                                                return n && nameSet.has(n);
                                                            });
                                                            // プルダウンの並び順（文字昇順）に合わせてテーブルもソート
                                                            const indexMap = new Map<string, number>(columnNameOptions.map((n: string, i: number) => [n, i]));
                                                            rows.sort((a: any, b: any) => {
                                                                const an = String(a?.name ?? '').trim();
                                                                const bn = String(b?.name ?? '').trim();
                                                                return (indexMap.get(an) ?? 0) - (indexMap.get(bn) ?? 0);
                                                            });
                                                            return rows.length > 0 ? (
                                                                rows.map((f: any, idx: number) => {
                                                                    const fname = String(f?.name ?? '');
                                                                    const dtype = String(f?.enlight_dtype ?? '');
                                                                    const linkedIds = linkedEnlightAttrIdsByName.get(fname) ?? [];
                                                                    const fnameLabel = linkedIds.length > 0
                                                                        ? `${fname}（連携済みID:${linkedIds.join(',')}）`
                                                                        : fname;
                                                                    return (
                                                                        <TableRow key={`${fname}-${idx}`} hover>
                                                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fnameLabel}</TableCell>
                                                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{dtype}</TableCell>
                                                                            <TableCell>
                                                                                <Tooltip title={String(f?.description ?? '')} arrow enterDelay={300}>
                                                                                    <Link component="button" onClick={(e: any) => e.preventDefault()} underline="hover" sx={{ cursor: 'help' }}>
                                                                                        説明
                                                                                    </Link>
                                                                                </Tooltip>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>データはありません。</TableCell>
                                                                </TableRow>
                                                            );
                                                        })()}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                    {/* Enlight連携設定 */}
                    {/* テキスト入力設定 */}
                    {newMeta && (newMeta.attr_type === 1 || newMeta.attr_type === 2 || newMeta.attr_type === 13) &&
                        <Card ref={refText} sx={{ backgroundColor: cardBg, my: 2, position: 'relative' }}>
                            <Typography variant="caption" sx={{ position: 'absolute', top: 6, left: 8, fontWeight: 600, zIndex: 1 }}>
                                テキスト入力設定
                            </Typography>
                            <CardContent sx={{ pt: 4 }}>
                                <Grid container spacing={2} className="search-row">
                                    <Grid size={2} display="flex" alignItems="center" justifyContent="center">
                                        <InputLabel
                                            htmlFor="min-length"
                                            sx={{
                                                minHeight: 32,
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 1,
                                            }}
                                        >
                                            最小文字数
                                        </InputLabel>
                                    </Grid>
                                    <Grid size={4} display="flex" alignItems="center" justifyContent="center">
                                        <TextField
                                            id="min-length"
                                            type="number"
                                            inputProps={{ min: 0, step: 1 }}
                                            sx={{
                                                backgroundColor: isDark ? '#1e293b' : 'white',
                                                minHeight: 40,
                                                '.MuiInputBase-input': { height: 40, boxSizing: 'border-box', padding: '8px 14px' }
                                            }}
                                            value={typeof (newMeta as any)?.min_length === 'number' ? (newMeta as any).min_length : ''}
                                            disabled={!canEdit || dialogDivision || !dialogFreeOption}
                                            onChange={(event) => setTextLength('min', event.target.value)}
                                        />
                                    </Grid>

                                    <Grid size={2} display="flex" alignItems="center" justifyContent="center">
                                        <InputLabel
                                            htmlFor="max-length"
                                            sx={{
                                                minHeight: 32,
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: 1,
                                            }}
                                        >
                                            最大文字数
                                        </InputLabel>
                                    </Grid>
                                    <Grid size={4} display="flex" alignItems="center" justifyContent="center">
                                        <TextField
                                            id="max-length"
                                            type="number"
                                            inputProps={{ min: 0, step: 1 }}
                                            sx={{
                                                backgroundColor: isDark ? '#1e293b' : 'white',
                                                minHeight: 40,
                                                '.MuiInputBase-input': { height: 40, boxSizing: 'border-box', padding: '8px 14px' }
                                            }}
                                            value={typeof (newMeta as any)?.max_length === 'number' ? (newMeta as any).max_length : ''}
                                            disabled={!canEdit || dialogDivision || !dialogFreeOption}
                                            onChange={(event) => setTextLength('max', event.target.value)}
                                            error={Boolean(textLenInvalid)}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    }
                    {/* テキスト入力設定 */}
                    {/* 値・色設定、および有効/無効化 */}
                    {newMeta && (newMeta.attr_type === 11 || newMeta.attr_type === 12 || newMeta.attr_type === 13) &&
                        <Card ref={refOptions} sx={{ backgroundColor: cardBg, position: 'relative' }}>
                            <Typography variant="caption" sx={{ position: 'absolute', top: 6, left: 8, fontWeight: 600, zIndex: 1 }}>
                                値・色設定、および有効/無効化
                            </Typography>
                            <CardContent sx={{ pt: 4 }}>
                                {scopedOptionsLoading && (
                                    <Typography data-testid="scoped-options-loading" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        選択中の課・チームの設定値を読み込んでいます…
                                    </Typography>
                                )}
                                {scopedOptionsError && (
                                    <Typography data-testid="scoped-options-error" variant="body2" color="error" sx={{ mb: 1 }}>
                                        設定値を取得できませんでした。画面を再読み込みしてください。
                                    </Typography>
                                )}
                                <Grid container spacing={2}>
                                    {/* 左：有効エリア */}
                                    <Grid size={6}>
                                        <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>有効</InputLabel>
                                        <TableContainer component={Paper} sx={{ overflowX: 'hidden' }} className={canEdit ? undefined : 'read-only-table'}>
                                            <Table stickyHeader aria-label="enabled table" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: !isDivisionDialog ? (effectiveDialogClassification ? '40%' : '60%') : undefined }}>値</TableCell>
                                                        {isTeamDialog && (<TableCell sx={{ width: '20%', minWidth: 96 }}>本文最大文字数</TableCell>)}
                                                        {isDivisionDialog && (<TableCell>Enlight同期デフォルト</TableCell>)}
                                                        {effectiveDialogClassification && (<TableCell sx={{ width: !isDivisionDialog ? '15%' : undefined }}>色</TableCell>)}
                                                        {effectiveDialogClassification && (<TableCell sx={{ width: !isDivisionDialog ? '25%' : undefined }}>{isWeeklyReportClassification ? '公開範囲' : '他課公開'}</TableCell>)}
                                                        <TableCell sx={{ width: !isDivisionDialog ? '20%' : 120, minWidth: !isDivisionDialog ? '20%' : 120, maxWidth: !isDivisionDialog ? '20%' : 120 }}>
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        color="success"
                                                                        onClick={handleAddClick}
                                                                        sx={{ minWidth: 64, width: 64, px: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
                                                                        disabled={!canEdit}
                                                                    >追加</Button>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {newMeta.options && newMeta.options.map((row: OptionsSelectList, index: number) => {
                                                        const enabled = (row as any).status !== 'N';
                                                        if (!enabled) return null;
                                                        const isNewRow = Boolean((row as any).__ui_new);
                                                        const uiRefOrg = (row as any).__ui_ref_org as any;
                                                        const showRefOrgIcon = isNewRow && isTeamDialog && uiRefOrg?.orgSelection === 'use';
                                                        const publishScopeLabel = getPublishScopeLabel(row);
                                                        return (
                                                            <TableRow
                                                                key={`enabled-${index}`}
                                                                data-ui-uid={(row as any).__ui_uid ?? ''}
                                                                data-ui-ref-org={uiRefOrg?.orgSelection === 'use' ? 'use' : ''}
                                                                data-ui-ref-division={uiRefOrg?.orgSelection === 'use' ? String(uiRefOrg?.divisionId ?? 0) : ''}
                                                                data-ui-ref-team={uiRefOrg?.orgSelection === 'use' ? String(uiRefOrg?.teamId ?? 0) : ''}
                                                                data-ui-ref-copy-enlight={uiRefOrg?.orgSelection === 'use' && uiRefOrg?.copySameEnlightConnection ? '1' : ''}
                                                                sx={isNewRow ? { backgroundColor: 'rgba(46, 125, 50, 0.06)' } : undefined}
                                                            >
                                                                <TableCell component="td" scope="row" sx={{ minWidth: 0 }}>
                                                                    <TextField
                                                                        required
                                                                        id="column-name"
                                                                        fullWidth
                                                                        size="small"
                                                                        sx={{ backgroundColor: isDark ? '#1e293b' : 'white' }}
                                                                        value={row.value}
                                                                        onChange={(event) => updateOptions(index, "value", event.target.value)}
                                                                        disabled={!canEdit}
                                                                        InputProps={isNewRow ? {
                                                                            sx: { pr: 0.5 },
                                                                            endAdornment: (
                                                                                <InputAdornment position="end" sx={{ ml: 0.5 }}>
                                                                                    <Tooltip title="新規追加行">
                                                                                        <Chip
                                                                                            icon={<NewReleasesIcon sx={{ fontSize: 14 }} />}
                                                                                            label="New"
                                                                                            size="small"
                                                                                            color="success"
                                                                                            variant="outlined"
                                                                                            sx={{
                                                                                                height: 18,
                                                                                                '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' },
                                                                                                '& .MuiChip-icon': { ml: 0.4, mr: -0.2 },
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                    {showRefOrgIcon && (
                                                                                        <Tooltip
                                                                                            placement="top"
                                                                                            title={
                                                                                                <Box sx={{ p: 0.5 }}>
                                                                                                    <Typography variant="caption" sx={{ display: 'block' }}>以下の課・チームのマスタ設定をコピーします。</Typography>
                                                                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                                                                        課: {divisionLabelById.get(Number(uiRefOrg?.divisionId)) || String(uiRefOrg?.divisionId ?? '-')}
                                                                                                    </Typography>
                                                                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                                                                        チーム: {teamLabelById.get(Number(uiRefOrg?.teamId)) || String(uiRefOrg?.teamId ?? '-')}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            }
                                                                                        >
                                                                                            <ErrorOutlineIcon sx={{ ml: 0.5, color: 'warning.main', cursor: 'help' }} fontSize="small" />
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </InputAdornment>
                                                                            )
                                                                        } : undefined}
                                                                    />
                                                                </TableCell>
                                                                {isTeamDialog && (
                                                                    <TableCell component="td" scope="row" sx={{ width: 110, minWidth: 96 }}>
                                                                        <TextField
                                                                            id={`opt-body-max-enabled-${index}`}
                                                                            type="number"
                                                                            inputProps={{ min: 0, step: 1 }}
                                                                            fullWidth
                                                                            size="small"
                                                                            sx={{ backgroundColor: isDark ? '#1e293b' : 'white', px: 0 }}
                                                                            value={typeof (row as any)?.body_max_length === 'number' ? (row as any).body_max_length : ''}
                                                                            onChange={(event) => updateOptions(index, "body_max_length", event.target.value)}
                                                                            placeholder={ "" + MAX_BODY_LENGTH }
                                                                            disabled={!canEdit}
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                                {isDivisionDialog && (
                                                                    <TableCell component="td" scope="row">
                                                                        <Switch
                                                                            checked={normalizeEnlightSyncDefault((row as any).enlight_sync_default)}
                                                                            onChange={(event) => setEnlightSyncDefault(index, event.target.checked)}
                                                                            disabled={!canEdit}
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                                {effectiveDialogClassification && (
                                                                    <TableCell component="td" scope="row">
                                                                        <input
                                                                            type="color"
                                                                            className="color-setting"
                                                                            style={{ width: 36, height: 36, padding: 0, border: 'none', background: 'transparent', borderRadius: '50%', cursor: 'pointer' }}
                                                                            value={row.color}
                                                                            onChange={(event) => updateOptions(index, "color", event.target.value)}
                                                                            disabled={!canEdit}
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                                {effectiveDialogClassification && (
                                                                    <TableCell component="td" scope="row" sx={{ minWidth: 0, overflow: 'hidden' }}>
                                                                        <Link
                                                                            data-testid={`publish-scope-trigger-${index}`}
                                                                            component="button"
                                                                            underline="hover"
                                                                            title={publishScopeLabel}
                                                                            sx={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}
                                                                            onClick={(e) => { e.preventDefault(); if (!canEdit) return; openPublishDialog(index, 'enabled'); }}
                                                                        >
                                                                            {publishScopeLabel}
                                                                        </Link>
                                                                    </TableCell>
                                                                )}
                                                                <TableCell component="td" scope="row" sx={{ width: !isDivisionDialog ? '20%' : 120, minWidth: !isDivisionDialog ? '20%' : 120, maxWidth: !isDivisionDialog ? '20%' : 120, p: 0.25 }}>
                                                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                                                        <Box display="flex" flexDirection="row" width="100%" sx={{ gap: 0 }}>
                                                                            <Box
                                                                                sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                                                                onClick={() => { if (!canEdit) return; if (findPrevEnabledIndex(index) !== -1) moveUp(index); }}
                                                                            >
                                                                                <ArrowUpwardIcon
                                                                                    sx={{
                                                                                        fontSize: 14,
                                                                                        color: findPrevEnabledIndex(index) === -1 ? 'text.disabled' : 'primary.main',
                                                                                        cursor: (!canEdit || findPrevEnabledIndex(index) === -1) ? 'default' : 'pointer',
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                            <Box
                                                                                sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                                                                onClick={() => { if (!canEdit) return; if (findNextEnabledIndex(index) !== -1) moveDown(index); }}
                                                                            >
                                                                                <ArrowDownwardIcon
                                                                                    sx={{
                                                                                        fontSize: 14,
                                                                                        color: findNextEnabledIndex(index) === -1 ? 'text.disabled' : 'primary.main',
                                                                                        cursor: (!canEdit || findNextEnabledIndex(index) === -1) ? 'default' : 'pointer',
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            color="warning"
                                                                            onClick={() => handleDisableOption(index, row)}
                                                                            sx={{ minWidth: 72, width: 72, px: 1, py: 0.25, fontSize: '0.7rem', mx: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
                                                                            disabled={!canEdit}
                                                                        >無効</Button>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                    {/* 右：無効エリア */}
                                    <Grid size={6}>
                                        <InputLabel sx={{ mb: 1, fontWeight: 'bold' }}>無効</InputLabel>
                                        <TableContainer component={Paper} sx={{ overflowX: 'hidden' }} className={canEdit ? undefined : 'read-only-table'}>
                                            <Table stickyHeader aria-label="disabled table" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: !isDivisionDialog ? (effectiveDialogClassification ? '45%' : '60%') : undefined }}>値</TableCell>
                                                        {isTeamDialog && (<TableCell sx={{ width: '20%', minWidth: 96 }}>本文最大文字数</TableCell>)}
                                                        {effectiveDialogClassification && (<TableCell sx={{ width: !isDivisionDialog ? '15%' : undefined }}>色</TableCell>)}
                                                        {effectiveDialogClassification && (<TableCell sx={{ width: !isDivisionDialog ? '20%' : undefined }}>{isWeeklyReportClassification ? '公開範囲' : '他課公開'}</TableCell>)}
                                                        <TableCell sx={{ width: !isDivisionDialog ? '20%' : 60, minWidth: !isDivisionDialog ? '20%' : 60, maxWidth: !isDivisionDialog ? '20%' : 60 }}>操作</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {newMeta.options && newMeta.options.map((row: OptionsSelectList, index: number) => {
                                                        const enabled = (row as any).status !== 'N';
                                                        if (enabled) return null;
                                                        const isNewRow = Boolean((row as any).__ui_new);
                                                        const uiRefOrg = (row as any).__ui_ref_org as any;
                                                        const showRefOrgIcon = isNewRow && isTeamDialog && uiRefOrg?.orgSelection === 'use';
                                                        const publishScopeLabel = getPublishScopeLabel(row);
                                                        return (
                                                            <TableRow
                                                                key={`disabled-${index}`}
                                                                data-ui-uid={(row as any).__ui_uid ?? ''}
                                                                data-ui-ref-org={uiRefOrg?.orgSelection === 'use' ? 'use' : ''}
                                                                data-ui-ref-division={uiRefOrg?.orgSelection === 'use' ? String(uiRefOrg?.divisionId ?? 0) : ''}
                                                                data-ui-ref-team={uiRefOrg?.orgSelection === 'use' ? String(uiRefOrg?.teamId ?? 0) : ''}
                                                                data-ui-ref-copy-enlight={uiRefOrg?.orgSelection === 'use' && uiRefOrg?.copySameEnlightConnection ? '1' : ''}
                                                                sx={{
                                                                    opacity: 0.85,
                                                                    ...(isNewRow ? { backgroundColor: 'rgba(46, 125, 50, 0.06)' } : {}),
                                                                }}
                                                            >
                                                                <TableCell component="td" scope="row" sx={{ minWidth: 0, width: !isDivisionDialog ? (effectiveDialogClassification ? '45%' : '60%') : undefined }}>
                                                                    <TextField
                                                                        required
                                                                        id="column-name"
                                                                        fullWidth
                                                                        size="small"
                                                                        sx={{ backgroundColor: isDark ? '#1e293b' : 'white' }}
                                                                        value={row.value}
                                                                        onChange={(event) => updateOptions(index, "value", event.target.value)}
                                                                        disabled={!canEdit}
                                                                        InputProps={isNewRow ? {
                                                                            sx: { pr: 0.5 },
                                                                            endAdornment: (
                                                                                <InputAdornment position="end" sx={{ ml: 0.5 }}>
                                                                                    <Tooltip title="新規追加行">
                                                                                        <Chip
                                                                                            icon={<NewReleasesIcon sx={{ fontSize: 14 }} />}
                                                                                            label="New"
                                                                                            size="small"
                                                                                            color="success"
                                                                                            variant="outlined"
                                                                                            sx={{
                                                                                                height: 18,
                                                                                                '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' },
                                                                                                '& .MuiChip-icon': { ml: 0.4, mr: -0.2 },
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                    {showRefOrgIcon && (
                                                                                        <Tooltip
                                                                                            placement="top"
                                                                                            title={
                                                                                                <Box sx={{ p: 0.5 }}>
                                                                                                    <Typography variant="caption" sx={{ display: 'block' }}>以下の課・チームのマスタ設定をコピーします。</Typography>
                                                                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                                                                        課: {divisionLabelById.get(Number(uiRefOrg?.divisionId)) || String(uiRefOrg?.divisionId ?? '-')}
                                                                                                    </Typography>
                                                                                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                                                                                        チーム: {teamLabelById.get(Number(uiRefOrg?.teamId)) || String(uiRefOrg?.teamId ?? '-')}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            }
                                                                                        >
                                                                                            <ErrorOutlineIcon sx={{ ml: 0.5, color: 'warning.main', cursor: 'help' }} fontSize="small" />
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </InputAdornment>
                                                                            )
                                                                        } : undefined}
                                                                    />
                                                                </TableCell>
                                                                {isTeamDialog && (
                                                                    <TableCell component="td" scope="row" sx={{ width: 110, minWidth: 96 }}>
                                                                        <TextField
                                                                            id={`opt-body-max-disabled-${index}`}
                                                                            type="number"
                                                                            inputProps={{ min: 0, step: 1 }}
                                                                            fullWidth
                                                                            size="small"
                                                                            sx={{ backgroundColor: isDark ? '#1e293b' : 'white' }}
                                                                            value={typeof (row as any)?.body_max_length === 'number' ? (row as any).body_max_length : ''}
                                                                            onChange={(event) => updateOptions(index, "body_max_length", event.target.value)}
                                                                            placeholder={ "" + MAX_BODY_LENGTH }
                                                                            disabled={!canEdit}
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                                {effectiveDialogClassification && (
                                                                    <TableCell component="td" scope="row" sx={{ width: !isDivisionDialog ? '15%' : undefined }}>
                                                                        <input
                                                                            type="color"
                                                                            className="color-setting"
                                                                            style={{ width: 36, height: 36, padding: 0, border: 'none', background: 'transparent', borderRadius: '50%', cursor: 'pointer' }}
                                                                            value={row.color}
                                                                            onChange={(event) => updateOptions(index, "color", event.target.value)}
                                                                            disabled={!canEdit}
                                                                        />
                                                                    </TableCell>
                                                                )}
                                                                {effectiveDialogClassification && (
                                                                    <TableCell component="td" scope="row" sx={{ minWidth: 0, overflow: 'hidden' }}>
                                                                        <Link
                                                                            component="button"
                                                                            underline="hover"
                                                                            title={publishScopeLabel}
                                                                            sx={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}
                                                                            onClick={(e) => { e.preventDefault(); if (!canEdit) return; openPublishDialog(index, 'disabled'); }}
                                                                        >
                                                                            {publishScopeLabel}
                                                                        </Link>
                                                                    </TableCell>
                                                                )}
                                                                <TableCell component="td" scope="row" sx={{ width: !isDivisionDialog ? '20%' : 60, minWidth: !isDivisionDialog ? '20%' : 60, maxWidth: !isDivisionDialog ? '20%' : 60, p: 0.25 }}>
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => setOptionStatus(index, true)}
                                                                        sx={{ width: 72, minWidth: 72, px: 1, py: 0.25, fontSize: '0.7rem', mx: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
                                                                        disabled={!canEdit}
                                                                    >有効</Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    }
                    {/* 値・色設定、および有効/無効化 */}
                </Box>
            </DialogContent>
            {/* 週報分類は公開範囲ダイアログ、それ以外は従来の他課公開ダイアログを使用 */}
            {isWeeklyReportClassification ? (
                <WeeklyReportPublishScopeDialog
                    open={publishDialogOpen}
                    onClose={closePublishDialog}
                    targetLabel={(() => {
                        if (!publishDialogTarget) return '';
                        const idx = publishDialogTarget.index;
                        const opt = (newMeta as any)?.options?.[idx];
                        return String(opt?.label ?? opt?.value ?? '');
                    })()}
                    targetColor={(() => {
                        if (!publishDialogTarget) return '';
                        const idx = publishDialogTarget.index;
                        const opt = (newMeta as any)?.options?.[idx];
                        return String(opt?.color ?? '');
                    })()}
                    departmentId={Number(division) || 0}
                    teamId={Number(team) || 0}
                    targetKey={publishDialogTarget ? String(
                        (newMeta as any)?.options?.[publishDialogTarget.index]?.option_id ??
                        (newMeta as any)?.options?.[publishDialogTarget.index]?.__ui_uid ??
                        publishDialogTarget.index
                    ) : ''}
                    initialPublic={(() => {
                        if (!publishDialogTarget) return null;
                        const idx = publishDialogTarget.index;
                        const opt = (newMeta as any)?.options?.[idx];
                        return (opt as any)?.public ?? null;
                    })()}
                    onApply={applyPublishScope}
                />
            ) : (
                <PublishScopeDialog
                    open={publishDialogOpen}
                    onClose={closePublishDialog}
                    targetLabel={(() => {
                        if (!publishDialogTarget) return '';
                        const idx = publishDialogTarget.index;
                        const opt = (newMeta as any)?.options?.[idx];
                        return String(opt?.label ?? opt?.value ?? '');
                    })()}
                    initialPublic={(() => {
                        if (!publishDialogTarget) return null;
                        const idx = publishDialogTarget.index;
                        const opt = (newMeta as any)?.options?.[idx];
                        return (opt as any)?.public ?? null;
                    })()}
                    onApply={applyPublishScope}
                />
            )}
            <OptionsAddDialog
                open={optionsAddDialogOpen}
                fullScreen={fullScreen}
                dialogTitle={`${title} 追加`}
                valueLabel={title}
                showOrgSelection={isTeamDialog}
                showColorSetting={Boolean(effectiveDialogClassification)}
                initialDivisionId={division}
                initialTeamId={team}
                orgSelection={optionsAddOrgSelection}
                divisionId={optionsAddDivisionId}
                teamId={optionsAddTeamId}
                onOrgSelectionChange={setOptionsAddOrgSelection}
                onDivisionIdChange={setOptionsAddDivisionId}
                onTeamIdChange={setOptionsAddTeamId}
                onClose={() => setOptionsAddDialogOpen(false)}
                onOk={handleOptionsAddOk}
                canEdit={canEdit}
            />
            <DialogActions>
                <Button data-testid="setting-dialog-update-button" variant="contained" onClick={handleAccept} disabled={!canEdit || isUpdateDisabled || isSaving || scopedOptionsLoading || scopedOptionsError}>更新する</Button>
                <Button data-testid="setting-dialog-cancel-button" disabled={isSaving} color="secondary" variant="outlined" onClick={handleClose}>キャンセル</Button>
            </DialogActions>
        </Dialog>
    );
}

 // プロパティ
SettingDialog.propTypes = {
    setting_id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    meta: PropTypes.object,
    rows: PropTypes.array,
    division: PropTypes.number.isRequired,
    team: PropTypes.number,
    dialogDivision: PropTypes.bool,
    dialogFreeOption: PropTypes.bool,
    dialogEditColor: PropTypes.bool,
    dialogClassification: PropTypes.bool,
    dialogFilterByTeam: PropTypes.bool,
    onAccept: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    digKey: PropTypes.string.isRequired,
    // 追加: Enlight連携関連
    enlightTeam: PropTypes.string,
    enlightSubteam: PropTypes.string,
    teamDocsetColumns: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default SettingDialog;
